import React, { useContext, useEffect, useRef, useState } from 'react';

import { ArrowRightIcon } from '$partials/icon';
import { Button, Select } from '$partials/ui';
import { formatPageTitle, formatPageUri, formatPrice, formatRentFrequency } from '$utilities/format';
import { ModalContext } from '$contexts/modal.jsx';
import { MultiSelectModal } from '$partials/modals';
import { useForm } from 'react-hook-form';
import Util from '$utilities';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */
const SALE = 'sale';
const RENT = 'rent';
const VALUTAION = 'valutaion';
const OTHER = 'other';
const FORM_ID = 'c5033353-5825-4ade-b5de-4c4d5ac21ed5'; //specific property
const OTHER_FORM_ID = '9d8347b8-7885-48e2-b690-120227bf45c3'; //generic enquiry
const REASONS = [
    {label:'Select', value: '', options: { hide: true }},
    {label:'I’d like to view a property', value: SALE},
    // {label:'I’d like to view a rental property', value: RENT},
    {label:'I’d like a valuation for my property', value: VALUTAION},
    {label:'Other', value: OTHER}
];
let formatter = Intl.NumberFormat('en-US', { notation: 'compact' });
let kFormatter = Intl.NumberFormat('en-US');

/**
 * @type {import('$types/components/partials/modules.d.ts').OffMarketForm}
 */
const OffMarketForm = ({
    hubspot,
    offMarketForm = {},
    page,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [hasSubmitted, setHasSubmitted] = useState(false);

    const [areaOptions, setAreaOptions] = useState([]);
    const [priceOptions, setPriceOptions] = useState([]);
    const [reason, setReason] = useState();
    const [selectedAreas, setSelectedAreas] = useState([]);
    const [selectedPriceBrackets, setSelectedPriceBrackets] = useState([]);
    const [minPrice, setMinPrice] = useState();
    const [maxPrice, setMaxPrice] = useState();
    const [property, setProperty] = useState();

    const { handleModal } = useContext(ModalContext);
    const containerRef = useRef();

    const getDefaultPrice = () => {
        const selectedMinPrice = minPrice || priceOptions[0]?.min;
        const selectedMaxPrice = maxPrice || priceOptions.reduce(
            (accumulator, currentValue) => accumulator.min > currentValue.min ? accumulator: currentValue, []
        )?.min;
        const defaultSelectedPrices = minPrice || maxPrice ? priceOptions?.filter((item) =>
            (item.min >= selectedMinPrice && item.min < selectedMaxPrice) ||  (item.max > selectedMinPrice && item.max <= selectedMaxPrice) || (!item.max && !maxPrice && item.min <= selectedMaxPrice)
        )?.map((priceBracket) => priceBracket.value) : [];

        return defaultSelectedPrices;
    };

    const formatPriceRange = (values) => {
        const validRanges = priceOptions.filter(range => values.includes(range.value));
        const formatMax = validRanges.reduce(
            (accumulator, currentValue) => accumulator.min > currentValue.min ? accumulator: currentValue, []
        );
        const maxPlus = !(formatMax?.max && formatMax?.min);
        const formatMin = validRanges.reduce(
            (accumulator, currentValue) => accumulator.min < currentValue.min ? accumulator: currentValue, []
        )?.min;

        return values.length === 1 && maxPlus ? `£${formatter.format(formatMax.min).replace('.', ',')}+` : `£${formatMin < 1000000 ? kFormatter.format(formatMin) : formatter.format(formatMin).replace('.', ',')} — £${formatter.format(maxPlus ? formatMax.min : formatMax.max).replace('.', ',')}${maxPlus ? '+' : ''}`;
    };

    const {
        handleSubmit,
        register,
        watch,
    } = useForm();
    useEffect(() => {
        setSelectedPriceBrackets(getDefaultPrice());
    }, [maxPrice, minPrice, priceOptions]);

    const formValues = watch();
    const isSharedFieldsValid = !!(formValues.firstname && formValues.lastname && formValues.email && formValues.phone);

    useEffect(() => {
        const activeForm = offMarketForm?.[property? 'property' : 'other'];
        const flattenedFields = activeForm.fieldGroups?.map((group) => group.fields).flat();
        const areas = flattenedFields?.filter((field) => field?.name === 'areas_requested')?.map((item) => item.options).flat() || [];

        const urlSearchString = window.location.search;
        const params = new URLSearchParams(urlSearchString);
        const areaParams = params.getAll('areas');
        const slug = params.getAll('slug');
        const type = params.getAll('type');

        setSelectedAreas(areas.filter(area => areaParams.includes(area.label.toLowerCase())).map(area=> area.label));
        setMinPrice(params.get('minPrice'));
        setMaxPrice(params.get('maxPrice'));
        setReason(params.get('type') || '');

        if (slug) {
            (async () => {
                try {
                    const response = await fetch(`/api/v1/property?slug=${slug}&type=${type}`, {
                        method: 'GET',
                        credentials: 'include',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-CSRF-Token': Util.cookies.getItem('crumb')
                        },
                    });

                    const result = await response.json();
                    if (result?.property?.live) {
                        setProperty(result?.property);
                    }
                } catch (err) {
                    console.error(err);
                }
            })();
        }
    }, []);

    useEffect(() => {
        const activeForm = offMarketForm?.[property? 'property' : 'other'];
        const flattenedFields = activeForm.fieldGroups?.map((group) => group.fields).flat();
        const areas = flattenedFields?.filter((field) => field?.name === 'areas_requested')?.map((item) => item.options).flat() || [];
        const priceBrackets = flattenedFields?.filter((field) => field?.name === (reason === SALE ? 'price_bracket' : 'rental_price_bracket'))?.map((item) => item.options).flat() || [];

        const priceBracketsWithMinMax = priceBrackets.map((item) => {
            let values;
            if(item.value.includes('-')) {
                values = item.value.split('-');
            } else if(item.value.includes('–')) {
                values = item.value.split('–');
            } else {
                values = item.value;
            }

            const regex = /\D+/gm;
            if(values.length === 2) {
                const min = values[0].replaceAll(regex, '');
                const max = values[1].replaceAll(regex, '');
                return {
                    ...item,
                    min: parseInt(min),
                    max: parseInt(max)
                };
            } else {
                const min = values.replaceAll(regex, '');
                return {
                    ...item,
                    min: parseInt(min),
                };
            }
        });

        setAreaOptions(areas);
        setPriceOptions(priceBracketsWithMinMax);
    }, [reason]);

    const submit = async (data) => {
        try {
            setIsLoading(true);
            const formFields = [
                {
                    objectTypeId: '0-1',
                    name: 'email',
                    value : data?.email
                },
                {
                    objectTypeId: '0-1',
                    name: 'firstname',
                    value : data?.firstname
                },
                {
                    objectTypeId: '0-1',
                    name: 'lastname',
                    value : data?.lastname
                },
                {
                    objectTypeId: '0-1',
                    name: 'phone',
                    value : data?.phone
                },
                {
                    objectTypeId: '0-1',
                    name: 'notes',
                    value : data.notes
                }
            ];
            const saleFields = [
                {
                    objectTypeId: '0-1',
                    name: 'price_bracket',
                    value : selectedPriceBrackets.join(';')
                },
                {
                    objectTypeId: '0-1',
                    name: 'areas_requested',
                    value : selectedAreas.join(';')
                },
                {
                    objectTypeId: '2-107254668',
                    name: 'enquiry_name',
                    value : `Off market enquiry — ${data?.firstname} ${data.lastname}`
                }
            ];
            const rentFields = [
                {
                    objectTypeId: '0-1',
                    name: 'rental_price_bracket',
                    value : selectedPriceBrackets.join(';')
                },
                {
                    objectTypeId: '0-1',
                    name: 'areas_requested',
                    value : selectedAreas.join(';')
                },
                {
                    objectTypeId: '2-107254668',
                    name: 'enquiry_name',
                    value : `Off market enquiry — ${data?.firstname} ${data.lastname}`
                }
            ];
            const valuationFields = [
                {
                    objectTypeId: '2-107254668',
                    name: 'evaluation_address',
                    value : data.address
                },
                {
                    objectTypeId: '2-107254668',
                    name: 'enquiry_name',
                    value : `Off market enquiry — ${data?.firstname} ${data.lastname}`
                }
            ];
            const otherFields = [
                {
                    objectTypeId: '2-107254668',
                    name: 'enquiry_name',
                    value : `Off market enquiry — ${data?.firstname} ${data.lastname}`
                }
            ];
            const propertyFields = property ? [
                {
                    objectTypeId: '0-1',
                    name: 'building_name',
                    value : property.buildingName || ''
                },
                {
                    objectTypeId: '0-1',
                    name: 'building_number',
                    value : property.buildingNumber || ''
                },
                {
                    objectTypeId: '0-1',
                    name: 'address_line_1',
                    value : property.addressLine1 || ''
                },
                {
                    objectTypeId: '0-1',
                    name: 'address_line_2',
                    value : property.addressLine2 || ''
                },
                {
                    objectTypeId: '0-1',
                    name: 'address_line_3',
                    value : property.addressLine3 || ''
                },
                {
                    objectTypeId: '0-1',
                    name: 'address_line_4',
                    value : `${property.addressLine4} ${property.postcode}`
                },
                {
                    objectTypeId: '2-107254668',
                    name: 'enquiry_name',
                    value : `Off market ${property.__typename === 'PropertyPage' ? 'sale' : 'let'} enquiry (${property.reapitId?.split('').slice(0, 3).join('')}) for ${property.addressLine1} ${property.priceGbp ? formatPrice(property.priceGbp) : `${formatPrice(property.rentGbp)} ${formatRentFrequency(property.rentFrequency)}`} — ${data?.firstname} ${data.lastname}`
                }
            ] : [];

            const response = await fetch('/api/crm/v1/form', {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': Util.cookies.getItem('crumb')
                },
                body: JSON.stringify({
                    formId: property ? FORM_ID : OTHER_FORM_ID,
                    fields: [...formFields, ...(property ? propertyFields : reason === SALE ? saleFields : reason === RENT ? rentFields : reason === VALUTAION ? valuationFields : reason === OTHER ? otherFields : [])],
                    context:  {
                        hutk: window.sessionStorage.getItem('hubspot_hubspotutk') || undefined,
                        pageUri: formatPageUri(),
                        pageName: formatPageTitle(page)
                    }
                })
            });

            const result = await response.json();

            if (response.status !== 200) {
                if (result && result.message) {
                    setMessage(result.message || 'Sorry, something went wrong, please try again later.');
                    return;
                } else {
                    throw new Error(response.status);
                }
            }
            const headerOffset = 80;
            const elementPosition = containerRef?.current?.getBoundingClientRect()?.top;
            const offsetPosition = elementPosition + window.scrollY - headerOffset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });

            setMessage(result?.message ||'Thank you for your enquiry');
            setHasSubmitted(true);
            setHasSubmitted(true);
            setIsLoading(false);
        } catch (err) {
            console.error(err);
            setMessage('Sorry, something went wrong, please try again later.');
            setIsLoading(false);
        }
    };

    return (
        <>
            <div
                className='bg-white trunk-full-width md-trunk-unset'
                ref={containerRef}>
                {hasSubmitted ? (
                    <div className='p5 md-py7 md-px6 col-12 md-col-6'>
                        <div className='type-style-2'>
                            Thank you for your enquiry
                        </div>
                        {message && (
                            <div
                                className='pt5 type-style-6'
                                dangerouslySetInnerHTML={{ __html: message}}/>
                        )}
                    </div>
                ) : (
                    <form
                        autoComplete='off'
                        data-form-id={FORM_ID}
                        data-portal-id={hubspot.portalId}
                        onSubmit={handleSubmit(submit)}>
                        <div className='flex flex-wrap p5'>
                            <div className='col-12 md-col-6'>
                                <div className='flex width-100'>
                                    <span className='absolute pl5 md-pl6 pb7 pt5 pointer-events-none'>
                                First Name *
                                    </span>
                                    <input
                                        {...register('firstname', { required: 'Please enter your first name.' })}
                                        required
                                        className={'border-top border-left border-right border-bottom width-100 pr6 pt7 pb6 md-pt8 md-pb7 type-style-6 pl5 md-pl6'}
                                        id='frm-firstname'/>
                                </div>
                            </div>
                            <div className='col-12 md-col-6'>
                                <div className='flex width-100 items-center'>
                                    <span className='absolute pl5 md-pl6 pb7 md-pb8 pointer-events-none'>
                                Last Name *
                                    </span>
                                    <input
                                        {...register('lastname', { required: 'Please enter your last name.' })}
                                        required
                                        className={'border-left border-right border-bottom md-border-0 md-border-right md-border-bottom width-100 pr6 pt7 pb6 md-pt8 md-pb7 type-style-6 pl5 md-pl6 md-border-top'}
                                        id='frm-lastname'/>
                                </div>
                            </div>
                            <div className='col-12 md-col-6'>
                                <div className='flex width-100 items-center'>
                                    <span className='absolute pl5 md-pl6 pb7 md-pb8 pointer-events-none'>
                                Email *
                                    </span>
                                    <input
                                        {...register('email', { required: 'Please enter your email.' })}
                                        required
                                        className={'border-left border-right border-bottom width-100 pr6 pt7 pb6 md-pt8 md-pb7 type-style-6 pl5 md-pl6'}
                                        id='frm-email'/>
                                </div>
                            </div>

                            <div className='col-12 md-col-6'>
                                <div className='flex width-100 items-center'>
                                    <span className='absolute pl5 md-pl6 pb7 md-pb8 pointer-events-none'>
                                Phone Number *
                                    </span>
                                    <input
                                        {...register('phone', { required: 'Please enter your phone number.' })}
                                        required
                                        className={'number-input border-left border-bottom border-right md-border-0 md-border-right md-border-bottom width-100 pr6 pt7 pb6 md-pt8 md-pb7 type-style-6 pl5 md-pl6'}
                                        id='frm-phone'
                                        type='number'/>
                                </div>
                            </div>

                            <div className='col-12'>
                                {/* If no query params */}
                                {!property && !reason && (
                                    <Select
                                        className='border-right border-left border-bottom off-market-select'
                                        label='Reason for contact'
                                        onChange={(value) => {
                                            setReason(value);
                                            setSelectedPriceBrackets([]);
                                        }}
                                        options={REASONS}
                                        value={reason} />
                                )}
                                {/* if query params, only render select after setReasons */}
                                {!property && reason && <Select
                                    className='border-right border-left border-bottom off-market-select'
                                    label='Reason for contact'
                                    onChange={(value) => {
                                        setReason(value);
                                        setSelectedPriceBrackets([]);
                                    }}
                                    options={REASONS}
                                    value={reason} />}
                            </div>

                            {!property && (reason === SALE || reason === RENT) && (
                                <div className='flex col-12 flex-col md-flex-row'>
                                    <button
                                        className='flex flex-col align-left width-100 py5 px5 md-py6 md-px6 border-0 border-left border-right border-bottom bg-white button'
                                        onClick={() => handleModal(
                                            <MultiSelectModal
                                                buttonLabel='Confirm Location'
                                                onComplete={setSelectedAreas}
                                                options={areaOptions}
                                                selected={selectedAreas}
                                                title='Preferred Locations'/>)}
                                        type='button'>
                                        <p className='type-style-5 pb4 md-pb5'>Preferred Locations *</p>
                                        <div className='flex flex-wrap items-center'>
                                            {selectedAreas.length > 0 ? selectedAreas.map((area, idx) => {
                                                return (
                                                    <p
                                                        className='type-style-6 mr2'
                                                        key={`off-market-${area}-${idx}`}>
                                                        {area}{selectedAreas.length > 1 && idx !== selectedAreas.length - 1 && ','}
                                                    </p>
                                                );
                                            }) : (
                                                <p className='type-style-6'>
                                                Select
                                                </p>
                                            )}
                                            <ArrowRightIcon className='mx5 mt1' />
                                        </div>
                                    </button>

                                    <button
                                        className='flex flex-col align-left width-100 py5 px5 md-py6 md-px6 border-0 border-left md-border-0 md-border-right md-border-bottom border-right border-bottom bg-white button'
                                        onClick={() => handleModal(
                                            <MultiSelectModal
                                                buttonLabel='Confirm Price'
                                                onComplete={setSelectedPriceBrackets}
                                                options={priceOptions.map(price => {
                                                    const maxPlus = !(price?.max && (price?.min || price.min === 0));
                                                    return {
                                                        ...price,
                                                        label: maxPlus ?
                                                            `£${formatter.format(maxPlus ? price.min : price.max).replace('.', ',')}+ ${reason === RENT ? 'P/W' : ''}`
                                                            :`£${price.min < 1000000 ? kFormatter.format(price.min) : formatter.format(price.min).replace('.', ',')} — £${formatter.format(price.max).replace('.', ',')} ${reason === RENT ? 'P/W' : ''}`
                                                    };
                                                })}
                                                selected={selectedPriceBrackets}
                                                title='Price Range' />)}
                                        type='button'>
                                        <p className='type-style-5 pb4 md-pb5'>Price Range *</p>
                                        <div className='flex items-center'>
                                            {selectedPriceBrackets.length > 0 ? (
                                                <p className='type-style-6'>
                                                    {formatPriceRange(selectedPriceBrackets)}{reason === RENT ? ' P/W' : ''}
                                                </p>
                                            ) : (
                                                <p className='type-style-6'>
                                                Select
                                                </p>
                                            )}
                                            <ArrowRightIcon className={`mx5 ${selectedPriceBrackets?.length > 0 ? '' : 'mt1'}`} />
                                        </div>
                                    </button>
                                </div>
                            )}

                            {reason === VALUTAION && (
                                <div className='col-12'>
                                    <div className='flex width-100 items-center'>
                                        <span className='absolute pl5 md-pl6 pb7 md-pb8 pointer-events-none'>
                                            Address *
                                        </span>
                                        <input
                                            {...register('address')}
                                            required
                                            className={'number-input border-left border-bottom border-right width-100 pr6 pt7 pb6 md-pt8 md-pb7 type-style-6 pl5 md-pl6'}
                                            id='frm-address' />
                                    </div>
                                </div>
                            )}

                            <div className='col-12'>
                                <div className='flex width-100 items-center relative'>
                                    <span className='absolute pl5 md-pl6 pb7 md-pb8 top-0 md-pt2 mt5 md-mt6 pointer-events-none'>
                                Your message
                                    </span>
                                    <textarea
                                        className='border-bottom border-left border-right width-100 type-style-6 pt8 pb6 md-pt10 md-pb7 pr6 pl5 md-pl6'
                                        placeholder='Type your message here...'
                                        rows={5}
                                        {...register('notes')}/>
                                </div>
                            </div>
                        </div>

                        <div className='md-col-5 pb5 px5'>
                            <Button
                                aria-label='Submit'
                                as='button'
                                className = 'bg-black white type-style-5 items-center'
                                disabled={isLoading || !isSharedFieldsValid || ((reason === SALE || reason === RENT) && !property && (!selectedPriceBrackets.length || !selectedAreas.length)) || (reason === VALUTAION && !formValues.address)}
                                look = 'fill'
                                renderIcon={<ArrowRightIcon />}
                                type='submit'>
                            Send
                            </Button>
                        </div>

                        {message && (
                            <div
                                className='pb5 width-100 px5'
                                dangerouslySetInnerHTML={{__html: message }} />
                        )}
                    </form>
                )}
            </div>
        </>
    );
};

/* =========================================================================  */
/* Typing and exports */
/* =========================================================================  */

export default OffMarketForm;
